import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { ZTreeModel, ExternalDataForTree } from '../models/ITreeModel';
declare var $: any;
@Injectable({
  providedIn: 'root',
})
export class TreeViewService {
  constructor() {}
  public nodeDropInGridSubject = new Subject<any>();

  /**
   *Create method for creating tree nodes
   *
   * @param {string} id
   * @param {string} name
   * @param {Array<ZTreeModel>} children
   * @param {ExternalDataForTree} data
   * @param {boolean} checked
   * @param {boolean} isParent
   * @param {boolean} nocheck
   * @param {string} [icon]
   * @param {number} [random]
   * @param {string} [title]
   * @param {string} [iconOpen]
   * @param {string} [iconClose]
   * @return {*}
   * @memberof TreeViewService
   */
  createNode(
    id: string,
    name: string,
    children: Array<ZTreeModel>,
    data: ExternalDataForTree,
    checked: boolean,
    isParent: boolean,
    nocheck: boolean,
    icon?: string,
    random?: number,
    title?: string,
    iconOpen?: string,
    iconClose?: string
  ) {
    return {
      id,
      name,
      children,
      data,
      checked,
      isParent,
      nocheck,
      icon,
      random,
      title,
      iconOpen,
      iconClose,
    } as ZTreeModel;
  }

  /**
   * Used to get Tree Object as per Tree Id
   *
   * @param {*} treeID
   * @return {*}  Tree Node
   * @memberof TreeViewService
   */
  getTreeObject(treeID: any) {
    return $.fn.zTree.getZTreeObj(treeID);
  }

  /**
   * Used to get Tree Node By Param
   *
   * @private
   * @param {*} treeID
   * @param {string} paramKey
   * @param {string} paramValue
   * @return {*} Tree Node
   * @memberof TreeViewService
   */
  private getTreeNodeByParam(
    treeID: any,
    paramKey: string,
    paramValue: string
  ) {
    try {
      var treeObj = this.getTreeObject(treeID);
      var node = treeObj.getNodeByParam(paramKey, paramValue);
      return node;
    } catch (ex) {}
  }

  /**
   * Used to insert Nodes
   *
   * @param {string} treeID
   * @param {*} parentNode
   * @param {Array<ZTreeModel>} nodes
   * @memberof TreeViewService
   */
  insertNodes(treeID: string, parentNode: any, nodes: Array<ZTreeModel>) {
    var zTree = this.getTreeObject(treeID);
    zTree.addNodes(parentNode, nodes);
  }

  /**
   * Used to get Tree Node By Id
   *
   * @param {*} treeID
   * @param {string} id
   * @return {*}  Tree Node
   * @memberof TreeViewService
   */
  public getTreeNodeById(treeID: any, id: string): ZTreeModel {
    return this.getTreeNodeByParam(treeID, 'id', id);
  }

  /**
   * Used to get Tree Node By Random Id
   *
   * @param {*} treeID
   * @param {string} id
   * @return {*} Tree Node
   * @memberof TreeViewService
   */
  public getTreeNodeByRandomId(treeID: any, id: string): ZTreeModel {
    return this.getTreeNodeByParam(treeID, 'random', id);
  }

  /**
   * Used to get Tree Node By Name
   *
   * @param {*} treeID
   * @param {string} name
   * @return {*} Tree Node
   * @memberof TreeViewService
   */
  public getTreeNodeByName(treeID: any, name: string) {
    return this.getTreeNodeByParam(treeID, 'name', name);
  }

  /**
   * Used to check if Node is Checked
   *
   * @param {*} treeID
   * @param {ZTreeModel} node
   * @return {*}  {boolean}
   * @memberof TreeViewService
   */
  public isNodeChecked(treeID: any, node: ZTreeModel): boolean {
    let Treenode: any = this.getTreeNodeById(treeID, node.id);
    return Treenode.checked;
  }

  /**
   * Used to Check Node
   *
   * @param {*} treeID
   * @param {ZTreeModel} node
   * @param {boolean} callback
   * @memberof TreeViewService
   */
  public checkNode(treeID: any, node: ZTreeModel, callback: boolean) {
    let treeObj = this.getTreeObject(treeID);
    let treeNode = this.getTreeNodeById(treeID, node.id);
    treeObj.checkNode(treeNode, true, false, callback);
  }

  /**
   * Used to remove all Child Node
   *
   * @param {*} treeID
   * @param {ZTreeModel} parentNode
   * @memberof TreeViewService
   */
  public removeAllChildNodes(treeID: any, parentNode: ZTreeModel) {
    let treeObj = this.getTreeObject(treeID);
    let treeNode = this.getTreeNodeById(treeID, parentNode.id);
    treeObj.removeChildNodes(treeNode);
  }

  /**
   * Used to get Selected Node
   *
   * @param {*} treeID
   * @param {ZTreeModel} node
   * @param {boolean} selectOtherNode
   * @memberof TreeViewService
   */
  public selectNode(treeID: any, node: ZTreeModel, selectOtherNode: boolean) {
    let treeObj = this.getTreeObject(treeID);
    let treeNode: any = this.getTreeNodeById(treeID, node.id);
    //this.currentSelectedNode = treeNode;
    treeObj.selectNode(treeNode, selectOtherNode);
  }

  /**
   * Used to get Parent Node
   *
   * @param {*} treeID
   * @param {ZTreeModel} node
   * @return {*} Tree Node
   * @memberof TreeViewService
   */
  public getParentNode(treeID: any, node: ZTreeModel) {
    let treeObj = this.getTreeObject(treeID);
    let treeNode: any = this.getTreeNodeById(treeID, node.id);
    return treeNode.getParentNode();
  }

  /**
   * Used to Delete Node
   *
   * @param {*} treeID
   * @param {ZTreeModel} node
   * @memberof TreeViewService
   */
  public deleteNode(treeID: any, node: ZTreeModel) {
    let treeObj = this.getTreeObject(treeID);
    let treeNode = this.getTreeNodeById(treeID, node.id);
    treeObj.removeNode(treeNode);
  }

  /**
   * Used to get First Node
   *
   * @param {*} treeID
   * @return {*} Tree Node
   * @memberof TreeViewService
   */
  public getFirstNode(treeID: any) {
    let treeObj = this.getTreeObject(treeID);
    var nodes: Array<ZTreeModel> = treeObj.getNodes();
    if (nodes.length > 0) {
      return nodes[0];
    } else {
      return null;
    }
  }

  /**
   * Used to update Nodes
   *
   * @param {*} treeID
   * @param {ZTreeModel} node
   * @memberof TreeViewService
   */
  public updateNode(treeID: any, node: ZTreeModel) {
    let treeObj = this.getTreeObject(treeID);
    let treeNode = this.getTreeNodeById(treeID, node.id);
    treeObj.updateNode(treeNode);
  }

  /**
   * Used to Deselect all Nodes as per Tree ID
   *
   * @param {*} treeID
   * @memberof TreeViewService
   */
  public deSelectAllNodes(treeID: any) {
    let treeObj = this.getTreeObject(treeID);
    //this.currentSelectedNode = undefined;
    if (treeObj) {
      treeObj.cancelSelectedNode();
    }
  }

  /**
   * Used to Deselect any Node as per Tree ID and node
   *
   * @param {*} treeID
   * @param {*} node
   * @memberof TreeViewService
   */
  public deSelectNode(treeID: any, node: any) {
    let treeObj = this.getTreeObject(treeID);
    //this.currentSelectedNode = undefined;
    if (treeObj) {
      treeObj.cancelSelectedNode(node);
    }
  }

  /**
   * Check If tree is initialized or not as per Tree ID
   *
   * @param {*} treeID
   * @return {*} Boolean
   * @memberof TreeViewService
   */
  public isTreeIntilized(treeID: any) {
    var zTree = this.getTreeObject(treeID);
    if (zTree) {
      return true;
    } else {
      return false;
    }
  }

  /**
   *  To get all nodes of Any Tree ID
   *
   * @param {*} treeID
   * @return {*} Array of {ZTreeModel}
   * @memberof TreeViewService
   */
  public getAllNodes(treeID: any) {
    let treeObj = this.getTreeObject(treeID);
    var nodes: Array<ZTreeModel> = treeObj.getNodes();
    return nodes;
  }

  /**
   * This function clears the tree by destroying it and then recreating it.
   * @param {any} treeID - The ID of the tree you want to clear.
   */
  public clearTree(treeID: any) {
    var zTreeObj = $.fn.zTree.getZTreeObj(treeID);
    if (zTreeObj) {
      this.deSelectAllNodes(treeID);
      zTreeObj.destroy();
    }
  }

  /**
   * Get the selected nodes from the tree object.
   * @param {any} treeID - The ID of the tree you want to get the selected nodes from.
   * @returns An array of selected nodes.
   */
  public getSelectedNodes(treeID: any) {
    let treeObj = this.getTreeObject(treeID);
    if (treeObj) {
      return treeObj.getSelectedNodes();
    }
  }
}
